import store from "../../vuex/store";
import { UserManager, Log } from "./oidcCompat";
import HandleError from "../errorHandler";
import Config from "../../appconfig.json";
import offlineConfig from "../../config/offlineMode";
import mockAuthService from "./mockAuthentication";
import { oidcConfig } from "./auth.config";

class AuthService {
  constructor() {
    this.userManager = new UserManager(oidcConfig);
    this.subscribed = false;

    Log.setLogger(console);
    Log.setLevel(process.env.NODE_ENV === "production" ? Log.WARN : Log.INFO);

    // Register event listeners on construction so token renewal always updates the store
    this.userManager.events.addUserLoaded(() => {
      store.dispatch("authentication/updateLoggedInStatus");
    });
    this.userManager.events.addUserUnloaded(() => {
      store.commit("authentication/logOut");
    });
    this.userManager.events.addUserSignedOut(() => {
      store.commit("authentication/logOut");
    });
    this.userManager.events.addAccessTokenExpired(() => {
      this.userManager.signinSilent().catch(() => {
        const returnUrl = window.location.hash
          ? window.location.hash.slice(1)
          : window.location.pathname;
        this.login(returnUrl);
      });
    });
    this.userManager.events.addAccessTokenExpiring(() => {
      console.log("Access token expiring");
    });
    this.userManager.events.addSilentRenewError((err) => {
      console.error("Silent renew error:", err);
      this.userManager.signinSilent().catch(() => {
        console.warn("Silent renew retry failed — will re-authenticate on expiry");
      });
    });
  }

  async login(returnUrl) {
    if (offlineConfig.isOfflineMode && offlineConfig.bypassAuth) {
      console.log("?? Offline mode: Using mock authentication");
      return mockAuthService.signinRedirect().then(() => {
        store.dispatch("authentication/updateLoggedInStatus");
      });
    }

    if (
      (Config.appOptions.forceDisableAuth ||
        window.webpackHotUpdate ||
        Config.platformEndpoints.authentication === "") &&
      !Config.appOptions.forceEnableAuth
    ) {
      store.dispatch("authentication/setLoginStateAsDev");
      return;
    }

    const user = await this.userManager.getUser();
    if (user && !user.expired) {
      console.log("Found user, therefore not redirecting");
      store.dispatch("authentication/updateLoggedInStatus");
      return;
    }

    console.log("No user found, redirecting to identity server");
    await this.userManager.signinRedirect({
      state: returnUrl || window.location.hash.slice(1) || window.location.pathname,
    }).catch((e) => {
      HandleError(e);
      throw new Error("Unable to log in");
    });
  }

  async handleCallback() {
    if (offlineConfig.isOfflineMode && offlineConfig.bypassAuth) {
      console.log("?? Offline mode: Mock signin callback");
      await mockAuthService.signinCallback();
      return undefined;
    }

    const user = await this.userManager.signinCallback(this.getNormalizedCallbackUrl());
    return user?.state;
  }

  getNormalizedCallbackUrl() {
    const href = window.location.href;
    const hash = window.location.hash || "";
    const queryStart = hash.indexOf("?");

    if (queryStart === -1) {
      return href;
    }

    // Hash mode callback URLs look like /#/callback?code=...&state=...
    // oidc-client-ts expects a standard query string for callback parsing.
    const query = hash.slice(queryStart + 1);
    return `${window.location.origin}${window.location.pathname}?${query}`;
  }

  async logout() {
    sessionStorage.removeItem("selectedMarketId");

    import("../../services/workspaceService").then((mod) => {
      if (
        mod.workspaceService &&
        typeof mod.workspaceService.clearAdoptETag === "function"
      ) {
        mod.workspaceService.clearAdoptETag();
      }
    });

    if (offlineConfig.isOfflineMode && offlineConfig.bypassAuth) {
      console.log("?? Offline mode: Mock logout");
      return mockAuthService.signoutRedirect();
    }

    const user = await this.userManager.getUser();
    const idTokenHint = user?.id_token;
    await this.userManager.removeUser();
    await this.userManager.signoutRedirect({ id_token_hint: idTokenHint });
  }

  async clearStaleState() {
    await this.userManager.clearStaleState();
  }

  async getUser() {
    if (offlineConfig.isOfflineMode && offlineConfig.bypassAuth) {
      return await mockAuthService.getUser();
    }

    const user = await this.userManager.getUser();
    if (!user || user.expired) return null;

    // Process user with the real token structure (similar to mock auth logic)
    const profile = user.profile;

    // Set SAS token
    store.commit("authentication/setSasToken", profile.sastoken);

    // Set permissions based on the real token structure
    // View NPD: typically based on having access to research or historical data
    const canViewNPD = !!(
      profile["ixp_researchxp.access"] === "true" || profile.role === "superUser"
    );
    store.commit("authentication/canViewNPD", canViewNPD);

    return user;
  }

  async renewToken() {
    return await this.userManager.signinSilent();
  }

  async getUserAccessToken() {
    if (offlineConfig.isOfflineMode && offlineConfig.bypassAuth) {
      return mockAuthService.getUserAccessToken();
    }

    const user = await this.getUser();
    if (user) console.info("Access token expires in", user.expires_in);
    return user ? user.access_token : "";
  }

  signinSilentCallback() {
    console.log("Silent callback");

    if (offlineConfig.isOfflineMode && offlineConfig.bypassAuth) {
      console.log("?? Offline mode: Mock silent callback");
      return mockAuthService
        .signinSilentCallback()
        .then((user) => console.log("Mock silent refresh successful", user))
        .catch((error) => console.error("Error in mock silent callback", error));
    }

    return this.userManager
      .signinSilentCallback(this.getNormalizedCallbackUrl())
      .then((user) => console.log("Successful refresh", user))
      .catch((error) => console.error("Error in silent callback", error));
  }

  changePassword() {
    var url = Config.platformEndpoints.authentication + "/password/change";
    window.open(url, "_blank");
  }
}

export const authService = new AuthService();

function parseAccessTokenPayload(accessToken) {
  if (!accessToken || typeof accessToken !== "string") return null;
  try {
    const parts = accessToken.split(".");
    if (parts.length < 2) return null;
    let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4 !== 0) {
      b64 += "=";
    }
    return JSON.parse(atob(b64 || "e30="));
  } catch (e) {
    return null;
  }
}

function numericClaim(payload, key) {
  if (!payload || typeof payload !== "object") return null;
  var raw = payload[key];
  if (typeof raw === "number" && isFinite(raw)) return raw;
  if (typeof raw === "string") {
    var n = Number(raw);
    if (isFinite(n)) return n;
  }
  return null;
}

function selectedTenantFromToken(accessToken) {
  var payload = parseAccessTokenPayload(accessToken);
  if (!payload) return "";
  var raw =
    payload.selected_tenant != null
      ? payload.selected_tenant
      : payload.selectedTenantId != null
        ? payload.selectedTenantId
        : payload.primary_tenant != null
          ? payload.primary_tenant
          : "";
  return raw != null ? String(raw).trim() : "";
}

function isIncomingTokenOlder(currentToken, incomingToken) {
  var currentPayload = parseAccessTokenPayload(currentToken);
  var incomingPayload = parseAccessTokenPayload(incomingToken);

  var currentWsa = numericClaim(currentPayload, "workspace_switched_at");
  var incomingWsa = numericClaim(incomingPayload, "workspace_switched_at");
  if (currentWsa != null && incomingWsa != null && incomingWsa !== currentWsa) {
    return incomingWsa < currentWsa;
  }
  if (currentWsa != null && incomingWsa == null) return true;
  if (currentWsa == null && incomingWsa != null) return false;

  var currentIat = numericClaim(currentPayload, "iat") || 0;
  var incomingIat = numericClaim(incomingPayload, "iat") || 0;
  return incomingIat < currentIat;
}

// ??? Backward-compatible named exports ????????????????????????????????????????
export function LogIn(returnUrl) {
  return authService.login(returnUrl);
}

export function SigninRedirect() {
  return authService
    .handleCallback()
    .then(() => store.dispatch("authentication/updateLoggedInStatus"))
    .catch((e) => HandleError(e));
}

export async function GetUser() {
  return authService.getUser();
}

export function GetUserAccessToken() {
  return authService.getUserAccessToken();
}

export function getUserManager() {
  return authService.userManager;
}

export function LogOut() {
  return authService.logout();
}

export function ChangePassword() {
  const url = Config.platformEndpoints.authentication + "/password/change";
  window.open(url, "_blank");
}

export function SigninSilentCallback() {
  return authService.signinSilentCallback();
}

export function HandleUnauthorised() {
  return authService.logout();
}

/**
 * Applies the new access_token from the workspace switch API so all subsequent API calls
 * use the new token (with updated selected_tenant). JWT tokens are immutable - we must
 * store and use this new token; the old one still has the old claims.
 */
export async function applyNewTokenFromWorkspaceSwitch(accessToken, expiresIn) {
  if (!accessToken || offlineConfig.isOfflineMode || offlineConfig.bypassAuth) {
    return;
  }
  var currentTokenFromStore =
    store.state.authentication && typeof store.state.authentication.accessToken === "string"
      ? store.state.authentication.accessToken
      : "";
  if (currentTokenFromStore && isIncomingTokenOlder(currentTokenFromStore, accessToken)) {
    var currentPayload = parseAccessTokenPayload(currentTokenFromStore);
    var incomingPayload = parseAccessTokenPayload(accessToken);
    console.warn("Skipping stale workspace token apply; current token is fresher", {
      currentTenant: selectedTenantFromToken(currentTokenFromStore) || null,
      incomingTenant: selectedTenantFromToken(accessToken) || null,
      currentWorkspaceSwitchedAt: numericClaim(currentPayload, "workspace_switched_at"),
      incomingWorkspaceSwitchedAt: numericClaim(incomingPayload, "workspace_switched_at"),
      currentIat: numericClaim(currentPayload, "iat"),
      incomingIat: numericClaim(incomingPayload, "iat"),
    });
    return;
  }
  const expires = typeof expiresIn === "number" ? expiresIn : 120;
  const expiresAt = Math.floor(Date.now() / 1000) + expires;

  store.commit("authentication/setAccessToken", accessToken);

  try {
    const user = await userManagerSingleton.getUser();
    let payload = {};
    try {
      const parts = String(accessToken).split(".");
      if (parts.length >= 2) {
        let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        while (b64.length % 4 !== 0) {
          b64 += "=";
        }
        payload = JSON.parse(atob(b64 || "e30="));
      } else {
        payload = {};
      }
    } catch (decodeErr) {
      // Some environments may return a non-JWT token shape; keep existing profile in that case.
      payload = {};
      console.warn("Could not decode token payload while applying workspace switch token:", decodeErr);
    }
    const profile = { ...(user && user.profile ? user.profile : {}), ...payload };
    const updatedUserPlain = {
      ...(user || {}),
      access_token: accessToken,
      expires_in: expires,
      expires_at: expiresAt,
      profile,
    };

    store.commit("authentication/setUser", updatedUserPlain);
    if (profile.name != null) {
      store.commit("authentication/setUserName", profile.name);
    }

    if (typeof userManagerSingleton.storeUser === "function" && user) {
      try {
        user.access_token = accessToken;
        user.expires_at = expiresAt;
        user.expires_in = expires;
        user.profile = profile;
        if (typeof user.toStorageString === "function") {
          userManagerSingleton.storeUser(user);
        }
      } catch (storeErr) {
        console.warn("Could not store new token in UserManager:", storeErr);
      }
    }

    // Await so callers (e.g. /adopt path) don't run before monitor user is updated for /ping.
    var crossSync = await import("../../services/workspaceCrossAppSync");
    if (crossSync.setMonitorCurrentUser && updatedUserPlain) {
      crossSync.setMonitorCurrentUser(updatedUserPlain);
    }
  } catch (e) {
    console.warn("Could not merge new token into user:", e);

    // Ensure monitor user still receives the updated token even if user/profile merge fails.
    try {
      var fallbackUser = {
        access_token: accessToken,
        expires_in: expires,
        expires_at: expiresAt,
        profile: store.state.authentication && store.state.authentication.user
          ? store.state.authentication.user.profile || {}
          : {},
      };
      var crossSyncFallback = await import("../../services/workspaceCrossAppSync");
      if (crossSyncFallback.setMonitorCurrentUser) {
        crossSyncFallback.setMonitorCurrentUser(fallbackUser);
      }
    } catch (fallbackErr) {
      console.warn("Could not set fallback monitor user after token apply failure:", fallbackErr);
    }
  }
}

export function redirectToIdPForNewTokenAfterWorkspaceSwitch() {
  if (offlineConfig.isOfflineMode && offlineConfig.bypassAuth) {
    return;
  }
  userManagerSingleton.signinRedirect({ extraQueryParams: { prompt: "none" } });
}
